/**
 * One feedback report (SA-1305): what they were doing, the conversation with Help, the steps before they sent
 * it, their browser and build, and a link to the page as they saw it. With `app_feedback:manage` the product
 * team sets the status (saved at once) and keeps a private note; both writes carry the report's stamp, and a
 * stale one keeps the typed note and offers Reload.
 */
import { useId, useState } from 'react'
import { ExternalLink, Mic } from 'lucide-react'
import {
  Button, Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle, NativeSelect, Textarea,
} from '@/components/ui'
import { FactList } from '@/components/records/FactList'
import { AnswerMarkdown } from '@/components/helpFeedback/AnswerMarkdown'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useDrawerWidth } from '@/hooks/useDrawerWidth'
import { isGone, useAdminFeedbackReport, usePatchAdminReport } from '@/hooks/useAdminFeedback'
import { usePermissionStore } from '@/stores/permissionStore'
import {
  isFeedbackChanged, type AdminMessage, type AdminPatch, type AdminReportDetail, type ReportStatus,
} from '@/services/helpFeedback/appFeedbackApi'
import { KindChip, StatusChip } from './feedbackChips'
import { FeedbackTimeline } from './FeedbackTimeline'
import { STATUSES, environmentFacts, pageHref, sentWords, timeline } from './feedbackAdminModel'
import * as T from './feedbackAdminText'

const QUIET = 'text-sm text-muted-foreground'
const HEADING = 'text-xs font-semibold uppercase tracking-wide text-muted-foreground'

function Section({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <h3 className={HEADING}>{title}</h3>
      {children}
    </section>
  )
}

function Turn({ m, zone }: { m: AdminMessage; zone: string }) {
  const help = m.role === 'assistant'
  const content = help ? (m.answer ?? m.text) : m.text
  return (
    <li className={help ? 'rounded-md bg-muted/40 px-3 py-2' : 'px-3 py-2'}>
      <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{help ? T.SPEAKER_HELP : T.SPEAKER_YOU}</span>
        {m.spoken && (
          <span className="inline-flex items-center gap-1" aria-label={T.SPOKEN}>
            <Mic aria-hidden="true" className="size-3" />
          </span>
        )}
        {m.at && <time dateTime={m.at}>{sentWords(m.at, new Date(), zone)}</time>}
      </div>
      {help ? <AnswerMarkdown content={content} /> : <p className="whitespace-pre-wrap text-sm [overflow-wrap:anywhere]">{content}</p>}
      {help && m.question && <p className="mt-1 text-sm font-medium">{m.question}</p>}
      {help && m.found && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="feedback-found">
          {T.FOUND_WORDS[m.found]}
          {m.sources && m.sources.length > 0 && ` · ${T.ANSWER_SOURCES}: ${m.sources.map((s) => s.title).join(', ')}`}
        </p>
      )}
    </li>
  )
}

function StaffBlock({ report, zone }: { report: AdminReportDetail; zone: string }) {
  const canManage = usePermissionStore((s) => s.isLoaded && s.permissions.has('app_feedback:manage'))
  const patch = usePatchAdminReport(report.id)
  const read = useAdminFeedbackReport(report.id)
  const ids = { status: useId(), note: useId() }
  const [typed, setTyped] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [conflict, setConflict] = useState(false)
  const [failed, setFailed] = useState(false)
  const note = typed ?? report.staff_note ?? ''
  const noteMeta = report.staff_note && report.staff_note_at
    ? T.noteBy(report.staff_note_by_name, sentWords(report.staff_note_at, new Date(), zone)) : null

  const send = (body: AdminPatch, onDone?: () => void) => {
    setSaved(false); setConflict(false); setFailed(false)
    patch.mutate({ ...body, expected_updated_at: report.updated_at }, {
      onSuccess: () => onDone?.(),
      onError: (err) => { if (isFeedbackChanged(err)) setConflict(true); else setFailed(true) },
    })
  }

  if (!canManage) {
    return (
      <Section title={T.STATUS_LABEL} testId="feedback-staff">
        <StatusChip status={report.status} />
        {report.staff_note && (
          <div className="space-y-1 pt-2">
            <h3 className={HEADING}>{T.NOTE_LABEL}</h3>
            <p className="whitespace-pre-wrap text-sm">{report.staff_note}</p>
            {noteMeta && <p className="text-xs text-muted-foreground">{noteMeta}</p>}
          </div>
        )}
      </Section>
    )
  }
  return (
    <section className="space-y-3 rounded-lg border border-border bg-muted/20 p-3" data-testid="feedback-staff">
      <div className="space-y-1">
        <label htmlFor={ids.status} className="text-sm font-medium">{T.STATUS_LABEL}</label>
        <div className="w-48">
          <NativeSelect id={ids.status} value={report.status} disabled={patch.isPending}
            onChange={(e) => send({ status: e.target.value as ReportStatus })}>
            {STATUSES.map((s) => <option key={s} value={s}>{T.STATUS_WORDS[s].label}</option>)}
          </NativeSelect>
        </div>
        <p className="text-xs text-muted-foreground">{T.STATUS_HINT}</p>
      </div>
      <div className="space-y-1">
        <label htmlFor={ids.note} className="text-sm font-medium">{T.NOTE_LABEL}</label>
        <Textarea id={ids.note} rows={3} value={note} onChange={(e) => { setTyped(e.target.value); setSaved(false) }} />
        <p className="text-xs text-muted-foreground">{noteMeta ?? T.NOTE_HINT}</p>
      </div>
      <div className="flex items-center gap-3">
        <Button type="button" size="sm" disabled={patch.isPending || typed === null || typed === (report.staff_note ?? '')}
          onClick={() => send({ staff_note: note.trim() ? note : null }, () => { setTyped(null); setSaved(true) })}>
          {T.SAVE_NOTE}
        </Button>
        <span role="status" className="text-xs text-muted-foreground">{saved ? T.NOTE_SAVED : ''}</span>
      </div>
      {conflict && (
        <div role="alert" className="flex items-center gap-3 text-sm text-destructive">
          {T.CHANGED_BY_SOMEONE}
          <Button type="button" variant="outline" size="sm"
            onClick={() => { setConflict(false); void read.refetch() }}>{T.RELOAD}</Button>
        </div>
      )}
      {failed && <p role="alert" className="text-sm text-destructive">{T.SAVE_FAILED}</p>}
    </section>
  )
}

function ReportBody({ report, zone }: { report: AdminReportDetail; zone: string }) {
  const { lines, errorCount } = timeline(report.context?.events, report.sent_at)
  const env = environmentFacts(report.context?.environment as Record<string, unknown> | undefined)
  const href = pageHref(report.page_info)
  const removed = T.removedLine(report.removed ?? [])
  return (
    <>
      <StaffBlock key={report.id} report={report} zone={zone} />
      <Section title={T.DOING_HEADING} testId="feedback-doing">
        {report.attempting && <p className="text-sm font-medium">{report.attempting}</p>}
        <p className="whitespace-pre-wrap text-sm [overflow-wrap:anywhere]">
          {report.summary ?? <span className="text-muted-foreground">{T.NO_SUMMARY}</span>}
        </p>
        {report.resolved_by_guidance && <p className={QUIET}>{T.HELP_ANSWERED}</p>}
      </Section>
      <Section title={T.CONVERSATION_HEADING} testId="feedback-conversation">
        {report.messages.length === 0
          ? <p className={QUIET}>{T.NO_CONVERSATION}</p>
          : <ol className="space-y-1">{report.messages.map((m, i) => <Turn key={i} m={m} zone={zone} />)}</ol>}
      </Section>
      <Section title={T.timelineHeading(errorCount)}>
        <FeedbackTimeline lines={lines} />
      </Section>
      {env.length > 0 && (
        <Section title={T.ENVIRONMENT_HEADING} testId="feedback-environment">
          <FactList facts={env.map((f) => ({ key: f.label, label: f.label, value: f.value }))} />
        </Section>
      )}
      {removed && (
        <Section title={T.NOT_ATTACHED_HEADING}>
          <p className={QUIET}>{removed}</p>
        </Section>
      )}
      {href && (
        <div className="space-y-1">
          <a href={href} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
            {T.OPEN_PAGE}
            <ExternalLink aria-hidden="true" className="size-3.5" />
          </a>
          <p className="text-xs text-muted-foreground">{T.OPEN_PAGE_HINT}</p>
        </div>
      )}
    </>
  )
}

export function FeedbackReportDrawer({ reportId, onClose }: { reportId: string | null; onClose: () => void }) {
  const resize = useDrawerWidth('app-feedback', { default: 640, min: 480 }, T.RESIZE_LABEL)
  const zone = useDefaultTimezone()
  const read = useAdminFeedbackReport(reportId)
  const report = read.data?.id === reportId ? read.data : undefined
  let body
  if (report) body = <ReportBody report={report} zone={zone} />
  else if (read.isError) body = <p role="alert" className="text-sm">{isGone(read.error) ? T.REPORT_GONE : T.REPORT_FAILED}</p>
  else body = <p role="status" className={QUIET}>{T.REPORT_LOADING}</p>
  return (
    <Drawer open={reportId !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <DrawerContent resize={resize} aria-label={T.DRAWER_LABEL}>
        <DrawerHeader>
          <DrawerTitle className="flex flex-wrap items-center gap-2">
            {report?.reference ?? T.DRAWER_LABEL}
            {report && <KindChip kind={report.kind} />}
            {report && <StatusChip status={report.status} />}
          </DrawerTitle>
          {report && (
            <p className="text-sm text-muted-foreground">
              {T.fromLine(report.sender_name, sentWords(report.sent_at, new Date(), zone))}
            </p>
          )}
        </DrawerHeader>
        <DrawerBody className="space-y-5">{body}</DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
